import { BookingProviderV2, useBookingV2 } from "context/BookingV2Context"
import { useCartDispatch } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import { useEffect } from "react"
import dynamic from "next/dynamic"
import { verifyToken } from "api/auth/verifyToken"
import {
    useQuery,
    useQueryClient,
    QueryClient,
    dehydrate,
} from "@tanstack/react-query"
import { useRouter } from "next/router"
import { useFeatureFlagPayload } from "posthog-js/react"
import { FeatureFlags } from "types/FeatureFlags"
import { fetchEvent } from "api"
import ActivityLoader from "components/atoms/ActivityLoader/ActivityLoader"
import PixelIntegrationComponent from "components/Analytics/PixelIntegrationComponent"
import { useUserContext } from "context/UserContext"
import { isEventExpired } from "utils/helpers/EventHelper"
import { queryKeys } from "utils/queryKeys"
import dayjs from "dayjs"
import { useLang } from "hooks/useLang"
import { isTimeslotDisabled } from "utils/helpers/bookingv2/ticket"
import { NextSeo } from "next-seo"

const BookingLayout = dynamic(() => import("components/Layout/BookingLayout"), {
    ssr: false,
})

type FeatureFlagPayload = {
    events?: string[]
}

interface BookEventProps {
    isWebView?: boolean
    appUserToken?: string
    dehydratedState?: unknown
}

export const getServerSideProps = async ({ req, params }) => {
    try {
        const queryClient = new QueryClient()

        const isWebView = req.headers.origin === "mobile_app"
        let appUserToken = ""
        let response: any = null

        if (isWebView) {
            appUserToken = req.headers.token
            if (!appUserToken) {
                return { notFound: true }
            }
            response = await verifyToken(appUserToken)
            if (!response?.success) {
                return { notFound: true }
            }
        }

        // SSR prefetch of event data so booking page renders on the server
        const rawEventId = params?.eventid as string
        if (rawEventId) {
            try {
                await queryClient.prefetchQuery({
                    queryKey: queryKeys.event(rawEventId),
                    queryFn: () => fetchEvent(rawEventId),
                })
            } catch (prefetchError) {
                console.error(
                    "Error prefetching event data on booking page:",
                    prefetchError
                )
            }
        }

        return {
            props: {
                appUserToken: isWebView ? appUserToken : null,
                isWebView,
                dehydratedState: dehydrate(queryClient),
            },
        }
    } catch (error) {
        console.error(error)
        return { notFound: true }
    }
}

const BookEvent = ({ appUserToken, isWebView }: BookEventProps) => {
    const cartDispatch = useCartDispatch()
    const router = useRouter()
    const { isAr, t } = useLang()
    const { setIsWebView, setToken } = useUserContext()
    // const queryClient = useQueryClient()

    const eventId = router.query.eventid as string
    // Get cached event data if available
    // const cachedEventData = queryClient.getQueryData<FetchEventResponse>(queryKeys.event(eventId))
    /*
     ** Event/tickets data is accessed from React Query's cache using the 'event' query key.
     ** If the cache is empty (ex: on the first load or cache invalidation), it triggers a fetch from the API.
     */
    const {
        data: fetchedEventData,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: queryKeys.event(eventId),
        queryFn: () => fetchEvent(eventId),
        // initialData: cachedEventData,
        enabled: !!eventId,
        staleTime: 300000, // 5 minutes (Consistent)
        gcTime: 600000, // 10 minutes
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
            // retry 3 times if the error is not "EVENT_NOT_FOUND"
            return error.message !== "EVENT_NOT_FOUND" && failureCount < 3
        },
    })

    // Log errors to console
    useEffect(() => {
        if (isError && error) {
            console.error("Error fetching event data:", error)
        }
    }, [isError, error])

    // Handle event not found error
    useEffect(() => {
        if (error instanceof Error && error.message === "EVENT_NOT_FOUND") {
            router.push("/404")
        }
    }, [error, router])

    // If we have cached data, store the event and tickets immediately
    const eventData = fetchedEventData?.payload
    const event = eventData?.event
    const tickets = eventData?.event?.time_slots || []

    const featureFlagPayload = useFeatureFlagPayload(
        FeatureFlags.BOOKING_V1
    ) as FeatureFlagPayload
    const flagEvents = featureFlagPayload?.events || []

    useEffect(() => {
        if (isWebView) {
            setIsWebView(true)
        }
        if (appUserToken) {
            setToken(appUserToken)
        }
    }, [appUserToken, isWebView, setToken, setIsWebView])

    useEffect(() => {
        if (
            (flagEvents && flagEvents.includes(eventId)) ||
            isEventExpired(event)
        ) {
            router.push(`/events/${eventId}`)
        }
    }, [flagEvents, eventId, router, event])

    useEffect(() => {
        if (!isLoading && event && tickets && event.availableTickets) {
            // Check if there are time slots
            if (tickets.length === 0) {
                router.push(`/events/${eventId}`)
                return
            }

            // Check if the event is expired
            const isEventExpired =
                event?.event_date?.ending_date &&
                new Date(event?.event_date?.ending_date) < new Date()

            // Check if all time slots are expired
            const isTimeSlotsExpired =
                tickets.length > 0 &&
                tickets.every(
                    (slot: { end_slot: string | Date }) =>
                        new Date(slot.end_slot) < new Date()
                )

            // Check if there are time slots but they're expired
            if (!isEventExpired && isTimeSlotsExpired) {
                router.push(`/events/${eventId}`)
                return
            }

            // For specific EID party events, skip directly to seat selection
            const eidPartyEvents = [
                process.env.NEXT_PUBLIC_EID_PARTY_1,
                process.env.NEXT_PUBLIC_EID_PARTY_2,
                process.env.NEXT_PUBLIC_EID_PARTY_3,
            ]

            // Add event ID to cart state for reference in other components
            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_EVENT,
                payload: { event: eventId },
            })

            if (eidPartyEvents.includes(eventId)) {
                // Check number of available timeslots
                const hasMultipleTimeslots = tickets.length > 1

                if (hasMultipleTimeslots) {
                    // If multiple timeslots, go to day selection step
                    cartDispatch({
                        type: ImprovedCartActionsEnum.SET_BOOKING_STEP,
                        payload: "DaySelection",
                    })
                } else {
                    // For single timeslot, auto-select it and go to seat selection
                    cartDispatch({
                        type: ImprovedCartActionsEnum.SET_BOOKING_STEP,
                        payload: "SelectSeats",
                    })

                    // Need to select the default day with proper initialization of all required fields
                    if (tickets.length > 0) {
                        const selectedTimeslot = tickets[0]
                        const masterTicketsMap = new Map(
                            event.availableTickets.map((t: any) => [t._id, t])
                        )

                        const resolvedTypesForDay = (
                            selectedTimeslot.types || []
                        )
                            .map((tsTypeFromSlot) => {
                                const masterTicketDetails: any =
                                    masterTicketsMap.get(tsTypeFromSlot.type_id)

                                if (!masterTicketDetails) {
                                    return null
                                }

                                const resolvedType = {
                                    _id: tsTypeFromSlot._id,
                                    limit: tsTypeFromSlot.limit,
                                    wait_list: tsTypeFromSlot.wait_list,
                                    on_hold: tsTypeFromSlot.on_hold,
                                    original_limit:
                                        tsTypeFromSlot.original_limit,
                                    type_id: masterTicketDetails._id,
                                    price: masterTicketDetails.price,
                                    en_title: masterTicketDetails.en_title,
                                    ar_title: masterTicketDetails.ar_title,
                                    en_description:
                                        masterTicketDetails.en_description,
                                    ar_description:
                                        masterTicketDetails.ar_description,
                                    color: masterTicketDetails.color,
                                    promotion: masterTicketDetails.promotion,
                                    available_qty: tsTypeFromSlot.limit,
                                    min: masterTicketDetails.min,
                                    max: masterTicketDetails.max,
                                    seatsio_category_id:
                                        masterTicketDetails.seatsio_category_id,
                                    group: masterTicketDetails.group,
                                }
                                return resolvedType
                            })
                            .filter(Boolean)

                        const defaultDay = {
                            ...selectedTimeslot,
                            dayName: dayjs(selectedTimeslot.start_slot).format(
                                "dddd"
                            ),
                            dayNumber: dayjs(
                                selectedTimeslot.start_slot
                            ).date(),
                            monthName: dayjs(
                                selectedTimeslot.start_slot
                            ).format("MMMM"),
                            monthNum:
                                dayjs(selectedTimeslot.start_slot).month() + 1,
                            yearNum: dayjs(selectedTimeslot.start_slot)
                                .year()
                                .toString(),
                            start: new Date(selectedTimeslot.start_slot),
                            end: new Date(selectedTimeslot.end_slot),
                            types: resolvedTypesForDay,
                            seats_season_id: event.seatsio?.workspace_id || "",
                            allow_using_coupon:
                                selectedTimeslot.allow_using_coupon,
                            is_special_day: selectedTimeslot.is_special_day,
                        }

                        cartDispatch({
                            type: ImprovedCartActionsEnum.ADD_DAY,
                            payload: defaultDay,
                        })
                    }
                }
            } else {
                // For non-EID events, check if single timeslot has available tickets
                const hasMultipleTimeslots = tickets.length > 1

                if (hasMultipleTimeslots) {
                    // If multiple timeslots, go to day selection step
                    cartDispatch({
                        type: ImprovedCartActionsEnum.SET_BOOKING_STEP,
                        payload: "DaySelection",
                    })
                } else if (tickets.length === 1) {
                    // For single timeslot, check if it has available tickets
                    const singleTimeslot = tickets[0]

                    if (isTimeslotDisabled(singleTimeslot)) {
                        // If timeslot is disabled (sold out or no tickets), go to day selection to show disabled state
                        cartDispatch({
                            type: ImprovedCartActionsEnum.SET_BOOKING_STEP,
                            payload: "DaySelection",
                        })
                    } else {
                        // If timeslot has available tickets, go to day selection for user to select
                        cartDispatch({
                            type: ImprovedCartActionsEnum.SET_BOOKING_STEP,
                            payload: "DaySelection",
                        })
                    }
                }
            }
        }
    }, [isLoading, event, tickets, eventId, router, cartDispatch])

    // Show error state if fetch failed
    if (isError && !event) {
        router.push("/404")

        return (
            <div
                className="h-screen w-screen bg-cover bg-center flex flex-col items-center justify-center"
                style={{
                    backgroundImage:
                        'url("/svg/v2/pass_ticket_fallback_image_light.png")',
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                }}
            >
                <div className="bg-black bg-opacity-70 p-8 rounded-xl max-w-md text-center">
                    <h2 className="text-white text-xl mb-4">
                        Failed to load event data
                    </h2>
                    <p className="text-gray-300 mb-6">
                        There was a problem loading this event.
                    </p>
                    <div className="flex justify-center gap-4">
                        <button
                            className="bg-mint-green-500 hover:bg-mint-green-600 text-white px-6 py-3 rounded-full"
                            onClick={() => refetch()}
                        >
                            Try Again
                        </button>
                        <button
                            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-full"
                            onClick={() => router.push(`/events/${eventId}`)}
                        >
                            Back to Event
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // Only show loading on initial data load, not during background refreshes
    // Simplified loading state: Show loader only during initial load when data isn't ready
    if (isLoading && !event) {
        // Check isLoading and if event data is not yet available
        if (isWebView && typeof window !== "undefined") {
            window.ReactNativeWebView?.postMessage?.(
                JSON.stringify({ isLoaded: false })
            )
        }

        return (
            <div
                className="h-screen w-screen bg-center bg-[rgb(57, 41, 134)]"
                // style={{
                //     backgroundImage:
                //         'url("/svg/v2/pass_ticket_fallback_image_light.png")',
                // }}
            >
                <ActivityLoader />
            </div>
        )
    }

    if (isWebView && typeof window !== "undefined") {
        window.ReactNativeWebView?.postMessage?.(
            JSON.stringify({ isLoaded: true })
        )
    }

    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath = router.asPath.split("#")[0].split("?")[0] || "/"
    const canonicalUrl = `${baseUrl}${currentPath}`

    return (
        <>
            <NextSeo
                title={`${isAr ? event?.ar_title : event?.en_title} | ${t("SITE_NAME")}`}
                canonical={canonicalUrl}
                languageAlternates={(() => {
                    const pathNoLocale = currentPath.replace(
                        /^\/(en-US|ar-SA)(?=\/|$)/,
                        ""
                    )
                    const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
                    const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`
                    return [
                        { hrefLang: "x-default", href: defaultHref },
                        { hrefLang: "ar", href: defaultHref },
                        { hrefLang: "en", href: enHref },
                    ]
                })()}
                noindex
                nofollow
            />
            <BookingProviderV2 event={event} tickets={tickets}>
                <PixelIntegrationComponent>
                    <BookingContent />
                </PixelIntegrationComponent>
            </BookingProviderV2>
        </>
    )
}

function BookingContent() {
    const { currentStep } = useBookingV2()

    return (
        <BookingLayout>
            {currentStep && <currentStep.component />}
        </BookingLayout>
    )
}

export default BookEvent
